import { ReactRenderer } from '@tiptap/react'
import { Extension } from '@tiptap/core'
import Suggestion from '@tiptap/suggestion'
import type { SuggestionOptions, SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion'
import {
  Heading1, Heading2, Heading3, List, ListOrdered, ListChecks,
  FileCode, Quote, Minus
} from 'lucide-react'
import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react'

interface CommandItem {
  title: string
  description: string
  icon: React.ReactNode
  command: (props: { editor: any; range: any }) => void
}

const commands: CommandItem[] = [
  {
    title: 'Heading 1',
    description: 'Large section heading',
    icon: <Heading1 className="h-4 w-4" />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 1 }).run()
    },
  },
  {
    title: 'Heading 2',
    description: 'Medium section heading',
    icon: <Heading2 className="h-4 w-4" />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run()
    },
  },
  {
    title: 'Heading 3',
    description: 'Small section heading',
    icon: <Heading3 className="h-4 w-4" />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 3 }).run()
    },
  },
  {
    title: 'Bullet List',
    description: 'Unordered list',
    icon: <List className="h-4 w-4" />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBulletList().run()
    },
  },
  {
    title: 'Numbered List',
    description: 'Ordered list',
    icon: <ListOrdered className="h-4 w-4" />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleOrderedList().run()
    },
  },
  {
    title: 'Task List',
    description: 'Checklist with checkboxes',
    icon: <ListChecks className="h-4 w-4" />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleTaskList().run()
    },
  },
  {
    title: 'Code Block',
    description: 'Fenced code block',
    icon: <FileCode className="h-4 w-4" />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run()
    },
  },
  {
    title: 'Blockquote',
    description: 'Indented quote',
    icon: <Quote className="h-4 w-4" />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBlockquote().run()
    },
  },
  {
    title: 'Divider',
    description: 'Horizontal rule',
    icon: <Minus className="h-4 w-4" />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setHorizontalRule().run()
    },
  },
]

interface CommandListRef {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean
}

const CommandList = forwardRef<CommandListRef, SuggestionProps<CommandItem>>(
  (props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const containerRef = useRef<HTMLDivElement>(null)

    const filteredItems = props.items

    useEffect(() => {
      setSelectedIndex(0)
    }, [filteredItems])

    const selectItem = useCallback(
      (index: number) => {
        const item = filteredItems[index]
        if (item) {
          props.command(item)
        }
      },
      [filteredItems, props]
    )

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: SuggestionKeyDownProps) => {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((prev) => (prev + filteredItems.length - 1) % filteredItems.length)
          return true
        }
        if (event.key === 'ArrowDown') {
          setSelectedIndex((prev) => (prev + 1) % filteredItems.length)
          return true
        }
        if (event.key === 'Enter') {
          selectItem(selectedIndex)
          return true
        }
        return false
      },
    }))

    if (filteredItems.length === 0) return null

    return (
      <div
        ref={containerRef}
        className="z-50 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
      >
        {filteredItems.map((item, index) => (
          <button
            key={item.title}
            onClick={() => selectItem(index)}
            className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
              index === selectedIndex
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-gray-100 text-gray-500">
              {item.icon}
            </span>
            <div>
              <div className="font-medium">{item.title}</div>
              <div className="text-xs text-gray-400">{item.description}</div>
            </div>
          </button>
        ))}
      </div>
    )
  }
)

CommandList.displayName = 'CommandList'

const suggestion: Omit<SuggestionOptions<CommandItem>, 'editor'> = {
  items: ({ query }) => {
    return commands.filter((item) =>
      item.title.toLowerCase().includes(query.toLowerCase())
    )
  },
  render: () => {
    let component: ReactRenderer<CommandListRef> | null = null
    let wrapper: HTMLDivElement | null = null

    return {
      onStart: (props) => {
        component = new ReactRenderer(CommandList, {
          props,
          editor: props.editor,
        })

        if (!props.clientRect) return

        wrapper = document.createElement('div')
        wrapper.style.position = 'fixed'
        wrapper.style.zIndex = '50'
        document.body.appendChild(wrapper)
        wrapper.appendChild(component.element)

        const rect = (props.clientRect as () => DOMRect)()
        wrapper.style.left = `${rect.left}px`
        wrapper.style.top = `${rect.bottom + 4}px`
      },
      onUpdate: (props) => {
        component?.updateProps(props)
        if (wrapper && props.clientRect) {
          const rect = (props.clientRect as () => DOMRect)()
          wrapper.style.left = `${rect.left}px`
          wrapper.style.top = `${rect.bottom + 4}px`
        }
      },
      onKeyDown: (props) => {
        if (props.event.key === 'Escape') {
          wrapper?.remove()
          return true
        }
        return component?.ref?.onKeyDown(props) ?? false
      },
      onExit: () => {
        wrapper?.remove()
        wrapper = null
        component?.destroy()
      },
    }
  },
}

export const SlashCommands = Extension.create({
  name: 'slashCommands',

  addOptions() {
    return {
      suggestion: {
        char: '/',
        command: ({ editor, range, props }: { editor: any; range: any; props: CommandItem }) => {
          props.command({ editor, range })
        },
        ...suggestion,
      },
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ]
  },
})
